<script setup>
const route = useRoute();

import singleevent from "../assets/gql/event.gql";
const { data } = await useAsyncQuery(singleevent, { slug: route.params.slug });
const event = ref(data.value.eventBySlug);

const partners = [
  ["cnaip.svg", "https://www.cnaip.cz/", 80],
  ["aws.svg", "https://aws.amazon.com/", 60],
  ["google.svg", "https://www.google.com", 90],
  ["csob.svg", "https://www.csob.cz/", 75],
  ["newtonmedia.svg", "https://www.newtonmedia.cz/"],
  ["q_miners.svg", "https://qminers.com/cs/"],
  ["sz.svg", "https://www.seznamzpravy.cz"],
  ["wired.svg", "https://www.wired.cz", 90],
];

const eventType = computed(() => {
  if (event.value.attend === 1) return "online";
  if (event.value.attend === 2) return "offline";
  return "hybrid";
});

const dynamicFontSize = ref("");

const metaOptions = {
  title: `${event?.value?.name} | DNY AI`,
  description: `${event?.value.location?.name}, ${event?.value.location.address}, ${event.value.location.city.name}`,
  keywords: event.value?.tags,
};
useMeta(metaOptions);

const ctas = {
  link: event.value.registration_link,
  type: "REGISTRACE",
};
function formatTime(input) {
  const [hours, minutes] = input.split(":");
  return `${hours}:${minutes}`;
}

function formatDate(dateString) {
  const [year, month, day] = dateString?.split("-");
  return `${parseInt(day)}. ${parseInt(month)}. ${parseInt(year)}`;
}

onMounted(() => {
  const isWideViewport = window.innerWidth > 1000;
  dynamicFontSize.value =
    event.value.name?.length > 40 && isWideViewport ? "font-size: 2.5vw;" : "";
});
const formatGoogleDateTime = (date, time) => {
  return `${date.replace(/-/g, "")}T${time.replace(/:/g, "")}`;
};

const googleCalendarLink = computed(() => {
  const startDateTime = formatGoogleDateTime(
    event.value.term,
    event.value.start_time
  );
  const endDateTime = formatGoogleDateTime(
    event.value.term,
    event.value.end_time
  );

  const baseLink = "https://calendar.google.com/calendar/r/eventedit";
  const params = {
    title: event.value.name,
    dates: `${startDateTime}/${endDateTime}`,
    location: `${event.value.location.name},${event.value.location.address},${event.value.location.city.name}`,
  };
  return `${baseLink}?text=${params.title.replace(/ /g, "+")}&dates=${
    params.dates
  }&location=${params.location.replace(/ /g, "+")}&sf=true&output=xml`;
});
</script>

<template>
  <div class="single-page">
    <Menu :short="true" />
    <div class="event-container">
      <div class="half half-color">
        <div
          class="flex content-box sticky text-container"
          :style="{
            'background-color': event?.targets[0]?.color || '#e9a9a8',
            color: useContrastColor(event?.targets[0]?.color || '#e9a9a8'),
            overflow: 'scroll',
          }"
        >
          <div>
            <p class="category">
              <b class="uppercase">
                <span v-for="(target, index) in event?.targets">
                  {{ target.name
                  }}<span v-if="index !== event.targets.length - 1">, </span>
                </span>
              </b>
            </p>
            <h1 :style="dynamicFontSize">{{ event.name }}</h1>
          </div>
          <div class="flex-between registration-content relative">
            <div>
              <div class="instruction">
                <p>Organizátor</p>
                <p>
                  <b>{{ event.organiser.name }}</b>
                </p>
              </div>
              <div class="instruction">
                <p>Termín</p>
                <a
                  :href="googleCalendarLink"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <p>
                    <b
                      >{{ formatDate(event.term) }} od
                      {{ formatTime(event.start_time) }} do
                      {{ formatTime(event.end_time) }}</b
                    >
                  </p></a
                >
              </div>
              <div class="instruction">
                <p>Místo</p>
                <a
                  :href="`https://www.google.com/maps/place/${event.location.address},${event.location.city.name}`"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <p>
                    <b
                      >{{ event.location.name }}, {{ event.location.address }},
                      {{ event.location.city.name }}</b
                    >
                  </p></a
                >
              </div>
              <div class="instruction mw-60">
                <p>Cena</p>
                <p>
                  <span v-if="event.price === 0" class="uppercase"
                    ><b>zdarma</b></span
                  >
                  <span v-else
                    ><b>{{ `${event.price},00 Kč` }}</b>
                    <span v-if="event.maxPrice"
                      ><b> - {{ `${event.maxPrice},00 Kč` }}</b></span
                    ></span
                  >
                </p>
              </div>
            </div>

            <div v-if="event.registration_link" class="socials registrace">
              <ButtonWithArrow
                :cta="ctas"
                :white="useContrastColor(event?.targets[0]?.color) === 'white'"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="half">
        <div class="text-container">
          <GoBack />

          <div class="flex flex-wrap">
            <button>
              {{ eventType }}
            </button>
            <button class="white" v-for="language in event.languages">
              {{ language }}
            </button>
            <button class="white" v-for="category in event.categories">
              {{ category.name }}
            </button>
            <button class="white" v-for="format in event.formats">
              {{ format.name }}
            </button>
          </div>
          <div v-html="event.content" class="content"></div>
          <div v-if="event.program.length">
            <h3>Program</h3>
            <div v-for="item in event.program" class="program">
              <h4>{{ item.start_time }}-{{ item.end_time }}</h4>
              <h3 class="program-title">{{ item.name }}</h3>
              <div v-html="item.content"></div>
              <p>
                <b>{{ item.speaker }}</b>
              </p>
            </div>
          </div>
        </div>

        <div class="text-container bg-black content-box">
          <div>
            <p class="category uppercase"><b>Kontakt na pořadatele akce</b></p>
            <h3 class="max600">{{ event.organiser.name }}</h3>
          </div>
          <div class="soc-container">
            <div
              v-for="social in event.organiser.social_networks"
              class="socials"
            >
              <ButtonWithArrow :cta="social" :white="true" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <LogoPartners
      title="Partneři akce"
      :items="event.partners"
      v-if="event.partners.length > 0"
    />
    <Logos title="Dny AI 2024" :items="partners" />
    <Footer />
  </div>
</template>

<style lang="scss">
$menu-xl: 140px;
$menu: 100px;

.single-page {
  padding-top: $menu-xl;
  @media (max-width: 1300px) {
    padding-top: $menu;
  }
  .event-container {
    display: flex;
    @media (max-width: 991px) {
      display: block;
    }
  }

  .content figcaption {
    display: none;
  }
  .socials {
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
  }
  .registration-content {
    align-items: flex-end;
  }
  button {
    background-color: transparent;
    border: 1px solid black;
    color: black;
    margin-bottom: 8px;
    margin-right: 8px;
    padding: 8px 12px;
    font-weight: 300;
    font-size: 1rem;
    letter-spacing: 0.4px;
    @media (max-width: 1300px) {
      font-size: 0.75rem;
    }
    @media (max-width: 600px) {
      font-size: 10px;
      padding: 4px 8px;
    }
  }

  .small-font {
    font-size: 1rem;
  }

  .program {
    margin-bottom: 64px;
  }
  .mw-60 {
    max-width: 60%;
  }
  .text-container,
  .content-box {
    padding: 48px !important;
    min-height: calc(100vh - $menu-xl);
    @media (max-width: 1300px) {
      padding: 32px !important;
      min-height: calc(100vh - $menu);
    }
    @media (max-width: 967px) {
      padding: 24px !important;
      min-height: auto;
    }
    @media (max-width: 500px) {
      padding: 16px !important;
      min-height: auto;
    }
  }

  .content-box {
    display: flex;
    max-height: calc(100vh - $menu-xl);
    @media (max-width: 1300px) {
      max-height: calc(100vh - $menu);
    }
    @media (max-width: 967px) {
      max-height: auto;
    }
    box-sizing: border-box;
    flex-direction: column;
    justify-content: space-between;
    position: sticky;
    top: $menu-xl;
    @media (max-width: 1300px) {
      top: $menu;
    }
    @media (max-width: 767px) {
      height: auto;
      display: block;
    }

    .instruction,
    .cta {
      margin-top: 20px;
      @media (max-width: 1300px) {
        margin-top: 0.75rem;
      }

      p {
        margin: 0;
        font-size: 1.25rem;
        @media (max-width: 1500px) {
          font-size: 1rem;
        }
        @media (max-width: 1200px) {
          font-size: 14px;
        }
        @media (max-width: 500px) {
          font-size: 12px;
        }
      }
    }
    button {
      margin-right: 12px;
    }
  }
  .text-container {
    h2 {
      font-size: 3rem;
      @media (max-width: 1500px) {
        font-size: 2.15rem;
      }
      @media (max-width: 700px) {
        font-size: 1.75rem;
      }
      @media (max-width: 500px) {
        font-size: 1.5rem;
      }
    }
    h3 {
      font-size: 2.75rem;
      @media (max-width: 1500px) {
        font-size: 2rem;
      }
      @media (max-width: 700px) {
        font-size: 1.5rem;
      }
    }
    p,
    li {
      @media (max-width: 1000px) {
        font-size: 16px;
      }
      @media (max-width: 600px) {
        font-size: 14px;
      }
      @media (max-width: 440px) {
        font-size: 12px;
      }
    }
  }

  .max600 {
    max-width: 600px;
    @media (max-width: 800px) {
      max-width: 70%;
      margin-bottom: 3rem;
    }
  }
  .half {
    img {
      height: 100%;
    }
    @media (max-width: 767px) {
      display: block;
    }
  }

  .flex {
    @media (max-width: 767px) {
      display: block;
    }
  }
  .sticky {
    @media (max-width: 991px) {
      position: static;
    }
  }

  .registrace {
    position: absolute;
    right: 0;
  }
  h1,
  h3 {
    font-size: 3.5vw;
    line-height: 1.2;
    color: inherit;
    @media (max-width: 1500px) {
      font-size: 3rem;
      margin-bottom: 2rem;
    }
    @media (max-width: 1200px) {
      font-size: 2.75rem;
    }
    @media (max-width: 1000px) {
      margin-bottom: 2rem;
    }
    @media (max-width: 900px) {
      font-size: 2rem;
    }
    @media (max-width: 600px) {
      font-size: 1.75rem;
    }
    @media (max-width: 440px) {
      font-size: 1.25rem;
    }
  }

  .registrace,
  .soc-container {
    margin-bottom: -0.25rem;
  }
  .category {
    margin-top: 0;
    margin-bottom: -1rem;
    font-size: 1.25rem;
    @media (max-width: 967px) {
      font-size: 1rem;
      margin-bottom: -0.5rem;
    }
    @media (max-width: 500px) {
      font-size: 12px;
    }
  }
}
</style>
